import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import { PERMISSIONS } from "@/config";
import { UserPermissions } from "@/services/user-permissions/user-permissions";

export interface SessionContextContract {
  username: string | null;
  isAuthenticated: boolean;
  permissions: PERMISSIONS[];
  hasPermission: (permission: string) => boolean;
}

const SessionContext = createContext<SessionContextContract>({
  username: null,
  isAuthenticated: false,
  permissions: [],
  hasPermission: () => false,
});

export const useSession = () => useContext(SessionContext);

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Omit<SessionContextContract, "hasPermission">>({
    username: null,
    isAuthenticated: false,
    permissions: [],
  });

  useEffect(() => {
    const userPermissions = UserPermissions.current;
    if (userPermissions.hasAuthorized) {
      setSession({
        username: userPermissions.username,
        isAuthenticated: userPermissions.isAuth,
        permissions: userPermissions.permissions,
      });
    }
  }, []);

  const value = useMemo<SessionContextContract>(
    () => ({
      ...session,
      hasPermission: (permission: string) => session.permissions.includes(permission as PERMISSIONS),
    }),
    [session]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};
